import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IDropdownOption } from "@fluentui/react/lib/Dropdown";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";

import { WithConfiguration } from "Components/Generic/Configuration";
import { WithTranslator } from "Components/Generic/Translator";
import {
  hasNoElements, nonNull, removeElement, safeUnique, unique
} from "Utils/Array";
import { updateProjectItem } from "Utils/Item";
import { joinNonEmpty } from "Utils/String";
import { Translator } from "Utils/Translator";
import { ICompany } from "Model/Storage/ICompany";
import { projectsUpdateStart } from "Store/Actions";
import { selectActiveProject } from "Store/Selectors/Projects";
import { OverflowDropdown } from "Components/Generic/OverflowDropdown";
import { getTranslatedFieldLabel } from "Utils/Field";
import { EntityType } from "Model/EntityType";
import { IOptionList } from "Model/Configuration/IOptions";
import { useAuthenticationPreflight } from "Components/Generic/Authentication";
import { useCompanyRoleStyles } from "./CompanyRoleStyles";
import { ICompanyRoleProps } from "./ICompanyRoleProps";

export const CompanyRole = WithConfiguration(
  WithTranslator((props: ICompanyRoleProps): JSX.Element => {
    const { config, translator, company } = props;
    const { preflightAuthentication } = useAuthenticationPreflight();
    const options = useMemo(
      () => toDropdownOptions(config.getOptionValues(`@targetRoles`, EntityType.Company), translator),
      [config, translator]
    );
    const [uniqueValues, setUniqueValues] = useState(nonNull(safeUnique<string | number>(company[`@targetRoles`])));
    const styles = useCompanyRoleStyles();

    const toolTipText = useMemo(
      () => joinNonEmpty(
        options.filter((o) => uniqueValues.includes(o.key)).map((o) => o.text),
        `, `
      ),
      [options, uniqueValues]
    );

    const project = useSelector(selectActiveProject);
    const dispatch = useDispatch();

    const onChange = useCallback(
      async (_, option?: IDropdownOption) => {
        const { key, selected } = option;
        const val = selected ? unique([...uniqueValues, key]) : removeElement(uniqueValues, key);
        setUniqueValues(val);

        const updatedProject = updateProjectItem(project, company[`@id`], (c: ICompany) => ({
          ...c,
          "@targetRoles": val
        }));

        await preflightAuthentication();
        dispatch(projectsUpdateStart([updatedProject]));
      },
      [company, dispatch, preflightAuthentication, project, uniqueValues]
    );

    return (
      <TooltipHost content={toolTipText}>
        <OverflowDropdown
          styleSet={styles}
          multiSelect
          onChange={onChange}
          options={options}
          placeholder={`${getTranslatedFieldLabel(
            `@targetRoles.option.placeholder`,
            EntityType.Company,
            translator,
            `Select`
          )}...`}
          selectedKeys={uniqueValues as string[] | number[]}
        />
      </TooltipHost>
    );
  })
);

function toDropdownOptions(optionValues: IOptionList, translator: Translator): IDropdownOption[] {
  if (optionValues == null) {
    return [];
  }

  if (hasNoElements(optionValues)) {
    return [];
  }

  return optionValues.map<IDropdownOption>((value) => ({
    key: value,
    text: getTranslatedFieldLabel(`@targetRoles.option.${value}`, EntityType.Company, translator)
  }));
}
